import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { IPerioChart } from '@dataTypes/chartingFeature.types'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import {
  isExplicitPerioHistorySelection,
  isPersistedPerioChart,
  resolveActivePerioChart,
} from '@utils/active-perio-chart.utils'
import {
  setCreatePerioChart,
  setSelectedPerioChart,
  upsertPerioChartInList,
} from '@store/reducers/charting.reducer'

export interface UseActivePerioChartReturn {
  activePerioChart: IPerioChart
  isExplicitHistorySelection: boolean
  isPersistedChart: boolean
  showExistingConditions: boolean
  commitActivePerioChartUpdate: (chart: IPerioChart) => void
}

export const useActivePerioChart = (): UseActivePerioChartReturn => {
  const dispatch = useAppDispatch()
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )
  const perioCharts = useAppSelector(
    (state) => state.charting.perioCharts ?? [],
  )
  const preferCreatePerioChart = useAppSelector(
    (state) => state.charting.preferCreatePerioChart,
  )
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const { showExistingConditions } = useClinicChartingPreferences()

  const activePerioChart = useMemo(
    (): IPerioChart =>
      resolveActivePerioChart({
        selectedPerioChart,
        createPerioChart,
        perioCharts,
        showExistingConditions,
        patientId: selectedPatient?.patient_id,
        preferCreatePerioChart,
      }),
    [
      selectedPerioChart,
      createPerioChart,
      perioCharts,
      showExistingConditions,
      selectedPatient?.patient_id,
      preferCreatePerioChart,
    ],
  )

  const isExplicitHistorySelection = useMemo(
    (): boolean =>
      isExplicitPerioHistorySelection(selectedPerioChart, createPerioChart),
    [selectedPerioChart, createPerioChart],
  )

  const isPersistedChart = useMemo(
    (): boolean => isPersistedPerioChart(activePerioChart, createPerioChart),
    [activePerioChart, createPerioChart],
  )

  const commitActivePerioChartUpdate = useCallback(
    (chart: IPerioChart): void => {
      if (isExplicitHistorySelection) {
        dispatch(setSelectedPerioChart(chart))
        dispatch(upsertPerioChartInList(chart))
        return
      }

      if (isPersistedChart) {
        dispatch(upsertPerioChartInList(chart))
        return
      }

      dispatch(setCreatePerioChart(chart))
    },
    [dispatch, isExplicitHistorySelection, isPersistedChart],
  )

  return {
    activePerioChart,
    isExplicitHistorySelection,
    isPersistedChart,
    showExistingConditions,
    commitActivePerioChartUpdate,
  }
}
